/** Estoque (docs/design/pranchas/12-estoque.png): a lista, o produto, o que dá para vender e o gasto de cada serviço. */
import {
  type MovementRow,
  type ProductSalesSummary,
  type ProductUse,
  type StockLevel,
  materialCost,
  movementRows,
  productSales,
  sellable,
  stockLevel,
  stockLevels,
  stockValue,
  suppliesOf,
  usesLeft,
  usesOf,
} from '../domain/stock';
import { startOfMonth, todayIn } from '../domain/time';
import type { EntityType, Product, Service, ServiceSupply, StockMovement } from '../schemas/entities';
import { useLiveQuery } from './index';

const STOCK: EntityType[] = ['product', 'stock_movement', 'service_supply', 'service', 'professional'];

export interface StockView {
  levels: StockLevel[];
  /** o que acabou ou está acabando (já vem primeiro em levels) */
  low: StockLevel[];
  valueCents: number;
  /** vendas de produto no mês até hoje */
  monthSales: ProductSalesSummary;
  /** nomes dos serviços que gastam cada produto */
  usedBy: Map<string, Service[]>;
}

export function useStock(): StockView | undefined {
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      const tz = prof?.timezone ?? 'America/Sao_Paulo';
      const today = todayIn(tz);
      const [products, movements, supplies, services] = await Promise.all([
        rt.db.all('product'),
        rt.db.all('stock_movement'),
        rt.db.all('service_supply'),
        rt.db.all('service'),
      ]);
      const levels = stockLevels(products, movements);
      const serviceById = new Map(services.filter((s) => s.active).map((s) => [s.id, s]));
      const usedBy = new Map<string, Service[]>();
      for (const s of supplies) {
        const service = serviceById.get(s.service_id);
        if (!service) continue;
        usedBy.set(s.product_id, [...(usedBy.get(s.product_id) ?? []), service]);
      }
      return {
        levels,
        low: levels.filter((l) => l.out || l.low),
        valueCents: stockValue(levels),
        monthSales: productSales(products, movements, startOfMonth(today), today, tz),
        usedBy,
      };
    },
    [],
    STOCK,
  );
}

export interface ProductDetail {
  level: StockLevel;
  uses: ProductUse[];
  /** "Dá para mais 5 manutenções" */
  left: ReturnType<typeof usesLeft>;
  rows: MovementRow[];
}

export function useProductDetail(id: string | null): ProductDetail | null | undefined {
  return useLiveQuery(
    async (rt) => {
      if (!id) return null;
      const product = await rt.db.get('product', id);
      if (!product || product.deleted_at) return null;
      const prof = await rt.db.get('professional', rt.db.professionalId());
      const [movements, supplies, services, appointments, clients, sales] = await Promise.all([
        rt.db.all('stock_movement'),
        rt.db.all('service_supply'),
        rt.db.all('service'),
        rt.db.all('appointment'),
        rt.db.all('client'),
        rt.db.all('sale'),
      ]);
      const level = stockLevel(product, movements);
      const uses = usesOf(product.id, supplies, services, appointments, Date.now());
      return {
        level,
        uses,
        left: usesLeft(level.qty, uses),
        rows: movementRows(product, movements, { appointments, clients, services, sales, timeZone: prof?.timezone ?? 'America/Sao_Paulo' }),
      };
    },
    [id],
    [...STOCK, 'appointment', 'client', 'sale'],
  );
}

/** Produtos com preço de venda e estoque, para cobrar e para a venda avulsa. */
export function useSellable(): StockLevel[] | undefined {
  return useLiveQuery(async (rt) => sellable(stockLevels(await rt.db.all('product'), await rt.db.all('stock_movement'))), [], ['product', 'stock_movement']);
}

export interface ServiceSuppliesView {
  items: Array<{ supply: ServiceSupply; product: Product; level: StockLevel }>;
  /** custo do material de cada atendimento */
  costCents: number;
  /** produtos ativos (para escolher o que adicionar) */
  levels: StockLevel[];
  /** movimentos, para a prévia do custo enquanto ela edita */
  movements: StockMovement[];
}

export function useServiceSupplies(serviceId: string | null): ServiceSuppliesView | undefined {
  return useLiveQuery(
    async (rt) => {
      const [products, movements, supplies] = await Promise.all([rt.db.all('product'), rt.db.all('stock_movement'), rt.db.all('service_supply')]);
      const byId = new Map(products.map((p) => [p.id, p]));
      const items = serviceId
        ? suppliesOf(serviceId, supplies)
            .map((supply) => {
              const product = byId.get(supply.product_id);
              return product && product.active ? { supply, product, level: stockLevel(product, movements) } : null;
            })
            .filter((x): x is NonNullable<typeof x> => x !== null)
        : [];
      return {
        items,
        costCents: serviceId ? materialCost(serviceId, supplies, products, movements) : 0,
        levels: stockLevels(products, movements),
        movements,
      };
    },
    [serviceId],
    ['product', 'stock_movement', 'service_supply'],
  );
}
